import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { PerformanceEvaluationView } from 'app/models/hris/PerformanceEvaluationView';

@Injectable({
	providedIn: 'root',
})
export class PerformanceEvaluationService {
	private readonly urlPath = `${environment.apiUrl}hris/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/performance-evaluation`, this.setupRequest(request, prefix))
			.pipe();
	}

	updateForm(clientId: string, id: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}`, this.setupRequest(request, prefix))
			.pipe();
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${
			this.urlPath
		}${clientId}/performance-evaluation/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/performance-evaluation/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/performance-evaluation/${formId}/docs/${docId}`);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/performance-evaluation/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/performance-evaluation/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	delegatedTo(clientId: string, formId: string, userId: any): Observable<PerformanceEvaluationView> {
		return this.httpClient
			.post<PerformanceEvaluationView>(`${this.urlPath}${clientId}/performance-evaluation/${formId}/delegate`, {
				userId,
			})
			.pipe();
	}
	getPerformanceEvaluationofEmployee(clientId: string, employeeId: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/performance-evaluation/employee/${employeeId}`).pipe();
	}
	private setupRequest(request: any, prefix: string) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));

		for (let i = 0; i < request.attachments.length; i++) {
			const name = `attachment-${i + 1}`;
			formData.append(name, request.attachments[i].file);
			request.attachments[i].name = name;
		}

		//Append Procurement Method Configuration Files
		for (let i = 0; i < request.performanceEvaluationMethod.performanceEvaluationMethodConfigurations.length; i++) {
			const configuration = request.performanceEvaluationMethod.performanceEvaluationMethodConfigurations[i];

			const isInputTypeFile = configuration.performanceEvaluationMethodTypeId == 1;
			if (isInputTypeFile) {
				formData.append(configuration.tempLabel.slice(prefix.length), request[configuration.tempLabel]);
			}
		}

		return formData;
	}
}
